import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Gift, Sparkles, Trophy } from 'lucide-react';
import { Button } from './ui/button';
import { Header } from './Header';
import { Footer } from './Footer';

export const SpinAndWin = () => {
  const canvasRef = useRef(null);
  const [isScratching, setIsScratching] = useState(false);
  const [scratchPercentage, setScratchPercentage] = useState(0);
  const [prize, setPrize] = useState(null);
  const [hasScratched, setHasScratched] = useState(false);
  const [selectedPrize, setSelectedPrize] = useState(null);

  const prizes = [
    { id: 1, name: '5% OFF Service', color: '#dc2626', description: 'Get 5% discount on any service' },
    { id: 2, name: 'Free AC Check', color: '#000000', description: 'Complimentary AC system inspection' },
    { id: 3, name: 'Oil Change Discount', color: '#dc2626', description: 'Save AED 20 on oil change' },
    { id: 4, name: 'Major Service 15% OFF', color: '#000000', description: '15% discount on major service' },
    { id: 5, name: 'Free Battery Check', color: '#dc2626', description: 'Complimentary battery health check' },
    { id: 6, name: 'Brake Service Discount', color: '#000000', description: 'Special discount on brake service' }
  ];

  // Initialize scratch card
  useEffect(() => {
    if (!hasScratched && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Set canvas size (MUCH smaller - 2 line height)
      const isMobile = window.innerWidth < 768;
      canvas.width = isMobile ? 300 : 400;
      canvas.height = isMobile ? 120 : 140; // Very small height - 2 lines
      
      // Draw scratch-off layer
      ctx.fillStyle = '#dc2626';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Add text (centered)
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 20px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('SCRATCH HERE', canvas.width / 2, canvas.height / 2 + 5);
      
      // Add sparkles on sides only
      ctx.font = '18px Arial';
      ctx.fillText('✨', 30, canvas.height / 2 + 5);
      ctx.fillText('✨', canvas.width - 30, canvas.height / 2 + 5);
    }
  }, [hasScratched]);

  // Select random prize when component mounts
  useEffect(() => {
    if (!selectedPrize) {
      const randomPrize = prizes[Math.floor(Math.random() * prizes.length)];
      setSelectedPrize(randomPrize);
    }
  }, []);

  const scratch = (e) => {
    if (hasScratched) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    let x, y;
    
    if (e.type.includes('touch')) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }
    
    // Scale for canvas resolution
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    x *= scaleX;
    y *= scaleY;
    
    // Scratch effect
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 30, 0, 2 * Math.PI);
    ctx.fill();
    
    // Check scratch percentage
    checkScratchPercentage();
  };

  const checkScratchPercentage = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    
    let transparentPixels = 0;
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] === 0) transparentPixels++;
    }
    
    const percentage = (transparentPixels / (pixels.length / 4)) * 100;
    setScratchPercentage(percentage);
    
    // Reveal prize IMMEDIATELY when 15% scratched
    if (percentage > 15 && !hasScratched) {
      setHasScratched(true);
      setPrize(selectedPrize);
    }
  };

  const handleMouseDown = () => setIsScratching(true);
  const handleMouseUp = () => setIsScratching(false);
  
  const handleMouseMove = (e) => {
    if (isScratching) scratch(e);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    scratch(e);
  };

  const claimPrize = () => {
    const message = `Hi Think Mechanic! 🎉 I just won "${prize.name}" from your Scratch & Win! I'd like to claim my prize and book a service.`;
    const whatsappNumber = '+971566671468';
    const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/\s/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const resetCard = () => {
    setHasScratched(false);
    setPrize(null);
    setScratchPercentage(0);
    const randomPrize = prizes[Math.floor(Math.random() * prizes.length)];
    setSelectedPrize(randomPrize);
  };

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>Scratch & Win - Think Mechanic Dubai | Win Exciting Discounts!</title>
        <meta name="description" content="Scratch the card and win exciting discounts on car services! Free AC check, oil change discount, brake service discount and more!" />
      </Helmet>

      <Header />

      {/* Hero Section */}
      <section className="relative py-12 sm:py-20 overflow-hidden bg-gradient-to-br from-black via-zinc-900 to-black">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, #dc2626 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-6 sm:mb-8 mt-4 sm:mt-8">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
              Scratch & Win
            </h1>
          </div>

          {/* Scratch Card Game */}
          <div className="max-w-3xl mx-auto">
            <div className="relative flex flex-col items-center">
              
              {/* Scratch Card Container */}
              <div className="relative bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-2xl p-4 sm:p-8 shadow-2xl border-4 border-red-600">
                
                {/* Prize Background (Hidden under scratch layer) */}
                {selectedPrize && (
                  <div 
                    className="absolute inset-4 sm:inset-4 rounded-xl flex items-center justify-center gap-3"
                    style={{ backgroundColor: selectedPrize.color }}
                  >
                    <Trophy className="w-10 h-10 sm:w-12 sm:h-12 text-yellow-400 flex-shrink-0" />
                    <h2 className="text-xl sm:text-2xl font-black text-white text-center">
                      {selectedPrize.name}
                    </h2>
                  </div>
                )}

                {/* Scratch Canvas */}
                <canvas
                  ref={canvasRef}
                  className="relative z-10 cursor-pointer touch-none rounded-xl"
                  style={{ maxWidth: '100%', height: 'auto' }}
                  onMouseDown={handleMouseDown}
                  onMouseUp={handleMouseUp}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseUp}
                  onTouchStart={handleMouseDown}
                  onTouchEnd={handleMouseUp}
                  onTouchMove={handleTouchMove}
                />

                {/* Instructions */}
                {!hasScratched && (
                  <div className="text-center mt-4">
                    <p className="text-gray-400 text-sm">
                      👆 Use your finger or mouse to scratch and reveal your prize
                    </p>
                  </div>
                )}
              </div>

              {/* Progress Indicator */}
              {!hasScratched && scratchPercentage > 0 && (
                <div className="mt-6">
                  <div className="bg-zinc-800 rounded-full h-3 w-64 overflow-hidden border border-red-600">
                    <div 
                      className="bg-gradient-to-r from-red-600 to-red-500 h-full transition-all duration-300"
                      style={{ width: `${Math.min(scratchPercentage, 100)}%` }}
                    />
                  </div>
                  <p className="text-gray-400 text-sm text-center mt-2">
                    {Math.floor(scratchPercentage)}% scratched - Keep going!
                  </p>
                </div>
              )}

              {/* Show winning message immediately after scratch */}
              {hasScratched && selectedPrize && !prize && (
                <div className="mt-4 sm:mt-8 bg-gradient-to-r from-red-600 to-red-700 rounded-xl p-4 sm:p-6 shadow-xl border-2 border-yellow-400 animate-bounce-in">
                  <p className="text-xl sm:text-2xl lg:text-3xl font-black text-yellow-400 text-center mb-1 sm:mb-2">
                    🎉 YOU WON! 🎉
                  </p>
                  <p className="text-lg sm:text-xl lg:text-2xl font-bold text-white text-center">
                    {selectedPrize.name}
                  </p>
                  <p className="text-white/90 text-center mt-1 sm:mt-2 text-sm sm:text-base">
                    {selectedPrize.description}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Prize Result */}
          {prize && (
            <div className="max-w-2xl mx-auto mt-8 sm:mt-16 animate-bounce-in">
              <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-2xl p-6 sm:p-8 text-center shadow-2xl border-4 border-yellow-400">
                <Trophy className="w-12 h-12 sm:w-16 sm:h-16 text-yellow-400 mx-auto mb-3 sm:mb-4 animate-pulse" />
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2 sm:mb-3">
                  🎉 Congratulations! 🎉
                </h2>
                <p className="text-lg sm:text-xl text-white font-semibold mb-1 sm:mb-2">
                  You won:
                </p>
                <p className="text-3xl sm:text-4xl font-black text-yellow-400 mb-2 sm:mb-3">
                  {prize.name}
                </p>
                <p className="text-white/90 mb-4 sm:mb-6 text-sm sm:text-base">
                  {prize.description}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                  <Button
                    onClick={claimPrize}
                    className="bg-white hover:bg-gray-100 text-red-600 px-6 sm:px-8 py-4 sm:py-6 rounded-lg text-base sm:text-lg font-bold transition-all duration-200 shadow-xl hover:scale-105"
                  >
                    Claim via WhatsApp
                    <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                  </Button>
                  
                  <Button
                    onClick={resetCard}
                    className="bg-yellow-400 hover:bg-yellow-500 text-black px-6 sm:px-8 py-4 sm:py-6 rounded-lg text-base sm:text-lg font-bold transition-all duration-200 shadow-xl hover:scale-105"
                  >
                    🎫 Try Again
                  </Button>
                </div>

                <p className="text-white/80 text-xs sm:text-sm mt-3 sm:mt-4">
                  📞 Contact: 056 667 1468
                </p>
              </div>
            </div>
          )}

          {/* All Prizes List */}
          <div className="max-w-4xl mx-auto mt-16">
            <h3 className="text-2xl font-bold text-white text-center mb-8">
              What You Can Win:
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {prizes.map((p) => (
                <div key={p.id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 hover:border-red-600 transition-all">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: p.color }}></div>
                    <h4 className="text-white font-bold">{p.name}</h4>
                  </div>
                  <p className="text-gray-400 text-sm">{p.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Terms */}
          <div className="max-w-2xl mx-auto mt-12 text-center">
            <p className="text-gray-500 text-sm">
              * Valid for one-time use. Must be claimed within 30 days. Cannot be combined with other offers. 
              Terms and conditions apply.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
